import { createContext, ReactNode, useEffect, useReducer } from "react";
import { ActiveCompetitionByTypeQuery } from "~graphql/sdk";
import { UserEntriesDocument } from "~graphql/typed-document-nodes";
import { useSDK } from "~hooks";
import { useQuery } from "~hooks/useQuery";
import { getGraphQLError, loadingPromise, Reconstruct } from "~lib";
import {
  PromoterUserAction,
  fromPromoterAction,
  isReferralPromoterAction,
  toPromoterAction,
} from "./helpers";

const CompetitionContext = createContext<CompetitionContextProps | undefined>(
  undefined
);

// TODO wording?
const alreadyEnteredErrorMessage = `Oops! It looks like you've already earned your entries from this.`;

export interface CompetitionState {
  value: number;
  isSubmitting: boolean;
  error?: string;
}

interface CompetitionContextState {
  entries: Record<PromoterUserAction, CompetitionState>;
  numberOfEntries: number;
}

interface CompetitionContextProps extends CompetitionContextState {
  competition: Reconstruct<
    ActiveCompetitionByTypeQuery["activeCompetitionByType"]
  >;
  grantCompetitionReward: (promoterAction: PromoterUserAction) => Promise<void>;
}

type CompetitionAction =
  | {
      type: "start";
      promoterAction: PromoterUserAction;
    }
  | {
      type: "success";
      promoterActions: {
        promoterAction: PromoterUserAction;
        value: number;
      }[];
    }
  | {
      type: "error";
      promoterAction: PromoterUserAction;
      error: string;
    };

const reducer = (state: CompetitionContextState, action: CompetitionAction) => {
  switch (action.type) {
    case "start":
      return {
        ...state,
        entries: {
          ...state.entries,
          [action.promoterAction]: {
            ...state.entries[action.promoterAction],
            isSubmitting: true,
          },
        },
      };
    case "success": {
      const newEntries = {
        ...state.entries,
        ...action.promoterActions.reduce((acc, { promoterAction, value }) => {
          let accValue = value;
          // A user can have multiple referral actions, unlike the other action types,
          // so we need to accumulate the values to show the correct number of entries.
          if (isReferralPromoterAction(promoterAction) && acc[promoterAction]) {
            accValue += acc[promoterAction].value;
          }

          return {
            ...acc,
            [promoterAction]: {
              value: accValue,
              isSubmitting: false,
              error: undefined,
            },
          };
        }, {} as Record<string, CompetitionState>),
      };

      return {
        ...state,
        numberOfEntries: Object.values(newEntries).reduce(
          (acc, { value }) => acc + value,
          0
        ),
        entries: newEntries,
      };
    }
    case "error":
      return {
        ...state,
        entries: {
          ...state.entries,
          [action.promoterAction]: {
            value: 0,
            isSubmitting: false,
            error: action.error,
          },
        },
      };
    default:
      throw new Error("Invalid action type");
  }
};

const CompetitionProvider = ({
  orderId,
  userId,
  competition,
  children,
}: {
  userId?: string;
  orderId?: string;
  competition?: ActiveCompetitionByTypeQuery["activeCompetitionByType"];
  children: ReactNode;
}) => {
  const sdk = useSDK();
  const isConfigured = !!competition && !!userId;
  const [state, dispatch] = useReducer(reducer, {
    numberOfEntries: 1,
    entries: {} as Record<PromoterUserAction, CompetitionState>,
  });

  const { data } = useQuery(isConfigured && UserEntriesDocument, {
    input: {
      competitionId: competition?.id,
      userId,
    },
  });

  useEffect(() => {
    if (!data?.userEntries) return;

    const newEntries = data.userEntries.map(
      ({ platform, actionType, value }) => ({
        promoterAction: toPromoterAction(platform, actionType),
        value,
      })
    );

    dispatch({
      type: "success",
      promoterActions: newEntries,
    });
  }, [data]);

  const grantCompetitionReward = async (promoterAction: PromoterUserAction) => {
    // Probably no competition running
    if (!isConfigured) {
      console.warn("No competition configured in context");
      return;
    }

    // Reward already granted
    if (state.entries[promoterAction]) {
      console.log("Already granted reward", promoterAction);
      return;
    }

    dispatch({ type: "start", promoterAction });

    const {
      platform: platformInput,
      actionType: actionTypeInput,
    } = fromPromoterAction(promoterAction);

    const { data, error } = await loadingPromise(async () =>
      sdk.grantCompetitionReward({
        input: {
          competitionId: competition?.id,
          userId,
          platform: platformInput,
          actionType: actionTypeInput,
          orderId,
        },
      })
    );

    if (error || !data?.grantCompetitionReward) {
      let errorMessage = `Sorry! Something went wrong. Please try again later.`;
      if (error) {
        const { message, statusCode } = getGraphQLError(error);
        // A 409 Confict error means the user has already performed this action
        // with this user/waitlist user previously.
        errorMessage =
          statusCode === 409 ? alreadyEnteredErrorMessage : message;
      }

      return dispatch({
        type: "error",
        promoterAction,
        error: errorMessage,
      });
    }

    const { platform, actionType, value } = data.grantCompetitionReward;
    dispatch({
      type: "success",
      promoterActions: [
        { promoterAction: toPromoterAction(platform, actionType), value },
      ],
    });
  };

  const contextState = {
    ...state,
    competition: isConfigured ? competition : undefined,
    grantCompetitionReward,
  };

  return (
    <CompetitionContext.Provider value={contextState}>
      {children}
    </CompetitionContext.Provider>
  );
};

export { CompetitionProvider, CompetitionContext };
